import {
  createPreviewRelease,
  deployViewerProject,
  fetchDeploymentJobStatus,
  fetchViewerProjectDraft,
  fetchViewerProjectStatus,
  saveViewerProjectDraft,
} from "../../../services/viewerProjectService";
import {
  mapDeployPayloadToDto,
  mapPreviewPayloadToDto,
  mapViewerJobStatusToDto,
  mapViewerProjectDraftToDto,
  mapViewerProjectStatusToDto,
} from "./mappers/viewerAuthoringMappers";
import type {
  ViewerAuthoringDraftDto,
  ViewerAuthoringDraftSaveRequestDto,
  ViewerAuthoringProjectStatusDto,
  ViewerDeploymentJobDto,
  ViewerDeployResultDto,
  ViewerPreviewReleaseDto,
} from "./types/viewerAuthoring.api";
import { uploadViewerEntityAsset } from "./viewerAssetsApi";

export async function getViewerProjectStatus(
  companyId: string,
  productId: string,
): Promise<ViewerAuthoringProjectStatusDto> {
  const status = await fetchViewerProjectStatus(companyId, productId);
  return mapViewerProjectStatusToDto(status);
}

export async function getViewerDraft(
  companyId: string,
  productId: string,
): Promise<ViewerAuthoringDraftDto | null> {
  const draft = await fetchViewerProjectDraft(companyId, productId);
  return draft ? mapViewerProjectDraftToDto(draft) : null;
}

export async function saveViewerDraft(
  companyId: string,
  productId: string,
  request: ViewerAuthoringDraftSaveRequestDto,
): Promise<ViewerAuthoringDraftDto> {
  const draft = await saveViewerProjectDraft(
    companyId,
    productId,
    request as unknown as { expectedRevision: number } & Record<string, unknown>,
  );
  return mapViewerProjectDraftToDto(draft);
}

export async function createViewerPreview(
  companyId: string,
  productId: string,
  idempotencyKey: string,
  expectedDraftRevision?: number,
): Promise<ViewerPreviewReleaseDto> {
  const payload = await createPreviewRelease(companyId, productId, idempotencyKey, expectedDraftRevision);
  return mapPreviewPayloadToDto(payload);
}

export async function deployViewerPreview(
  companyId: string,
  productId: string,
  idempotencyKey: string,
  sourceReleaseId?: string,
): Promise<ViewerDeployResultDto> {
  const payload = await deployViewerProject(companyId, productId, idempotencyKey, sourceReleaseId);
  return mapDeployPayloadToDto(payload);
}

export async function getViewerDeploymentJob(
  companyId: string,
  productId: string,
  jobId: string,
): Promise<ViewerDeploymentJobDto> {
  const jobStatus = await fetchDeploymentJobStatus(companyId, productId, jobId);
  return mapViewerJobStatusToDto(jobStatus);
}

export const createViewerPreviewRelease = createViewerPreview;
export const deployViewerAuthoringProject = deployViewerPreview;
export const fetchViewerAuthoringJobStatus = getViewerDeploymentJob;

export async function fetchViewerAuthoringStatus(
  companyId: string,
  productId: string,
) {
  const status = await getViewerProjectStatus(companyId, productId);
  return {
    companyId: status.companyId,
    productId: status.productId,
    state: status.lifecycleState,
    hasLiveDeployment: status.hasLiveDeployment,
    currentPreviewReleaseId: status.currentPreviewReleaseId,
    currentLiveReleaseId: status.currentLiveReleaseId,
    previewUrl: status.previewUrl,
    liveUrl: status.liveUrl,
    contentVersion: status.contentVersion,
    updatedAt: status.updatedAtIso,
  };
}

export async function fetchViewerAuthoringDraft(
  companyId: string,
  productId: string,
) {
  const draft = await getViewerDraft(companyId, productId);
  if (!draft) {
    return null;
  }

  return {
    companyId: draft.companyId,
    productId: draft.productId,
    revision: draft.revision,
    timelineSteps: draft.timelineSteps,
    visibilityAssignments: draft.visibilityAssignments,
    tools: draft.tools,
    parts: draft.parts,
    lastFrame: draft.lastFrame,
    updatedByUserId: draft.updatedByUserId,
    updatedAt: draft.updatedAtIso,
  };
}

export async function saveViewerAuthoringDraft(params: {
  companyId: string;
  productId: string;
  expectedRevision: number;
  body: Omit<ViewerAuthoringDraftSaveRequestDto, "expectedRevision">;
}) {
  return saveViewerDraft(params.companyId, params.productId, {
    expectedRevision: params.expectedRevision,
    ...params.body,
  });
}

export async function uploadViewerAuthoringAsset(params: {
  companyId: string;
  productId: string;
  entityType: "project" | "tool" | "part";
  entityId: string;
  file: File;
}): Promise<string> {
  const asset = await uploadViewerEntityAsset({
    companyId: params.companyId,
    productId: params.productId,
    entityType: params.entityType,
    entityId: params.entityId,
    assetKind: "image",
    file: params.file,
  });

  return asset.assetUrl;
}
